"use client";

import {
  ToolCallMessagePartComponent,
  useAui,
} from "@assistant-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { PaperclipIcon } from "lucide-react";
import { apiAuthFetch } from "@/lib/authFetch";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ClientLogger } from "@/lib/clientLogger";
import { submitToolCallResult } from "./ask-submit";
import { StepperFooter, StepperHeader } from "./stepper-shell";
import { useToolCardGating } from "./use-tool-card-gating";
import { useStepperNavigation } from "./use-stepper-navigation";

// Mirror of backend AskTool args shape (src/backend/AiAssistant.Api/Services/Tools/AskTool.cs).
type AskOption = {
  id: string;
  label: string;
  allowFile?: boolean;
  fileRequired?: boolean;
};

type AskQuestion = {
  id: string;
  header: string;
  question: string;
  options: AskOption[];
  type?: "single" | "multi";
};

type AskArgs = { questions: AskQuestion[] };

type AnswerFile = {
  optionId: string;
  fileId: string;
  fileName: string;
  note: string | null;
};

type AskAnswer = {
  questionId: string;
  selectedOptionIds: string[];
  freeText: string | null;
  files: AnswerFile[];
};

const ACCEPTED_EXTENSIONS = ".docx,.txt";

// UI-only sentinel for the synthetic "type your own answer" option row.
// Never transmitted to the backend — filtered out in onSubmit before building the payload.
// Double-underscore prefix is collision-safe: the LLM is instructed to use kebab-case option ids.
const OPEN_OPTION_ID = "__open__";

/**
 * Ask card — cut-turn flow.
 *
 * The backend ends the assistant turn when the `ask` tool fires; the card is rendered
 * from the persisted tool-call part's arguments. On submit the card POSTs the structured
 * payload to `/chat/tool-call/{id}/result` (for audit + file-handle lookup) and then
 * appends a prose `Q: … / A: …` user message via the assistant-ui runtime, which fires a
 * fresh `/chat` turn. The card unmounts; no summary is shown in the transcript.
 *
 * Refresh behaviour:
 * - Mid-card (no submit yet): tool-call part has empty `result` → card re-renders fresh
 *   (local `useState` resets on mount — partial answers are intentionally not persisted).
 * - After submit: the submit endpoint rewrites the persisted tool-call part's content with
 *   the prose summary, so `result` is non-empty → card renders null and only the Q/A user
 *   message remains visible.
 */
export const AskCard: ToolCallMessagePartComponent = ({
  toolCallId,
  argsText,
  status,
}) => {
  const { t } = useTranslation();
  const gating = useToolCardGating<AskArgs>(toolCallId, argsText, status);

  if (gating.state === "hidden") return null;
  if (gating.state === "loading") {
    return (
      <div className="aui-ask-card-loading rounded-lg bg-background px-4 py-3 text-sm text-muted-foreground">
        {t("ask.loading")}
      </div>
    );
  }

  return (
    <AskCardForm
      toolCallId={toolCallId}
      args={gating.args}
      onSubmitted={gating.onSubmitted}
    />
  );
};

// ─── Stepper form ────────────────────────────────────────────────────────────

type PerQuestionState = {
  selectedIds: Set<string>;
  freeText: string;
  filesByOption: Record<string, File | null>;
  notesByOption: Record<string, string>;
};

function emptyState(): PerQuestionState {
  return { selectedIds: new Set(), freeText: "", filesByOption: {}, notesByOption: {} };
}

const AskCardForm: React.FC<{
  toolCallId: string;
  args: AskArgs;
  onSubmitted: () => void;
}> = ({ toolCallId, args, onSubmitted }) => {
  const aui = useAui();
  const {
    stepIndex, setStepIndex, current, total, goBack, goNext, isLast,
    submitting, setSubmitting, errorMessage, setErrorMessage,
  } = useStepperNavigation(args.questions);
  const [states, setStates] = useState<Record<string, PerQuestionState>>(() => {
    const init: Record<string, PerQuestionState> = {};
    for (const q of args.questions) init[q.id] = emptyState();
    return init;
  });
  const [skipped, setSkipped] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const q of args.questions) init[q.id] = false;
    return init;
  });

  const { t } = useTranslation();
  const curState = states[current.id] ?? emptyState();
  const curSkipped = skipped[current.id] ?? false;
  const multi = current.type === "multi";
  const openSelected = curState.selectedIds.has(OPEN_OPTION_ID);

  // Any user interaction with a question's body un-skips it — keeps the model coherent
  // with what the user actually did (mirrors ask-open's setCurrentText behaviour).
  const unskipCurrent = useCallback(() => {
    setSkipped(prev => (prev[current.id] ? { ...prev, [current.id]: false } : prev));
  }, [current.id]);

  const toggle = useCallback((optionId: string) => {
    unskipCurrent();
    setStates(prev => {
      const cur = prev[current.id] ?? emptyState();
      const next = new Set(cur.selectedIds);
      if (multi) {
        if (next.has(optionId)) next.delete(optionId); else next.add(optionId);
      } else {
        next.clear();
        next.add(optionId);
      }
      return { ...prev, [current.id]: { ...cur, selectedIds: next } };
    });
  }, [current.id, multi, unskipCurrent]);

  const setFreeText = useCallback((text: string) => {
    if (text.length > 0) unskipCurrent();
    setStates(prev => {
      const cur = prev[current.id] ?? emptyState();
      return { ...prev, [current.id]: { ...cur, freeText: text } };
    });
  }, [current.id, unskipCurrent]);

  const setFile = useCallback((optionId: string, file: File | null) => {
    if (file) unskipCurrent();
    setStates(prev => {
      const cur = prev[current.id] ?? emptyState();
      return { ...prev, [current.id]: { ...cur, filesByOption: { ...cur.filesByOption, [optionId]: file } } };
    });
  }, [current.id, unskipCurrent]);

  const setNote = useCallback((optionId: string, note: string) => {
    if (note.length > 0) unskipCurrent();
    setStates(prev => {
      const cur = prev[current.id] ?? emptyState();
      return { ...prev, [current.id]: { ...cur, notesByOption: { ...cur.notesByOption, [optionId]: note } } };
    });
  }, [current.id, unskipCurrent]);

  const skipCurrent = useCallback(() => {
    setSkipped(prev => ({ ...prev, [current.id]: true }));
    setStates(prev => ({ ...prev, [current.id]: emptyState() }));
  }, [current.id]);

  // A question is answered if the user picked at least one real option (with file requirements
  // satisfied), OR if the "type your own answer" row is selected AND the textarea is non-empty.
  const isAnswered = useCallback((q: AskQuestion, s: PerQuestionState): boolean => {
    const openSelected = s.selectedIds.has(OPEN_OPTION_ID);
    const realIds = Array.from(s.selectedIds).filter(id => id !== OPEN_OPTION_ID);

    if (realIds.length === 0 && !openSelected) return false;

    // File requirements on real options.
    for (const opt of q.options) {
      if (!s.selectedIds.has(opt.id)) continue;
      if (opt.fileRequired && !s.filesByOption[opt.id]) return false;
    }

    // Open row selected but textarea is empty — not yet answered.
    if (openSelected && s.freeText.trim().length === 0) return false;

    return true;
  }, []);

  // A step is "decided" if answered OR explicitly skipped — both unblock Next/Submit.
  const isDecided = useCallback(
    (q: AskQuestion) =>
      (skipped[q.id] ?? false) || isAnswered(q, states[q.id] ?? emptyState()),
    [isAnswered, skipped, states],
  );

  const canAdvance = isDecided(current);
  const canSubmit = useMemo(
    () => args.questions.every(q => isDecided(q)),
    [args.questions, isDecided],
  );

  // Build the Q:/A: prose user message from the current state — one block per question.
  // Used both for the prose user message that starts the next turn and as a user-facing
  // preview on the last step. Skipped questions render a localised marker so the model
  // sees fidelity about which questions were intentionally skipped vs. answered.
  const buildProse = useCallback((finalStates: Record<string, PerQuestionState>, finalSkipped: Record<string, boolean>): string => {
    const blocks: string[] = [];
    for (const q of args.questions) {
      if (finalSkipped[q.id]) {
        blocks.push(`Q: ${q.question}\nA: ${t("ask.skippedMarker")}`);
        continue;
      }
      const s = finalStates[q.id] ?? emptyState();
      const openRowSelected = s.selectedIds.has(OPEN_OPTION_ID);
      const chosenLabels = Array.from(s.selectedIds)
        .filter(id => id !== OPEN_OPTION_ID)
        .map(id => q.options.find(o => o.id === id)?.label ?? id);
      const free = openRowSelected ? s.freeText.trim() : "";
      const parts: string[] = [];
      if (chosenLabels.length > 0) parts.push(chosenLabels.join(", "));
      if (free.length > 0) parts.push(`"${free}"`);
      // File filenames inline per option that had an attachment.
      const fileTags: string[] = [];
      for (const [optId, file] of Object.entries(s.filesByOption)) {
        if (!file) continue;
        const label = q.options.find(o => o.id === optId)?.label ?? optId;
        const note = (s.notesByOption[optId] ?? "").trim();
        fileTags.push(`[file attached to "${label}": ${file.name}${note ? ` — note: "${note}"` : ""}]`);
      }
      const answerText = parts.length > 0 ? parts.join(" · ") : "—";
      const fileText = fileTags.length > 0 ? `\n${fileTags.join("\n")}` : "";
      blocks.push(`Q: ${q.question}\nA: ${answerText}${fileText}`);
    }
    return blocks.join("\n\n");
  }, [args.questions, t]);

  const onSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);

    try {
      // Upload files first, then assemble structured payload. Skipped questions emit
      // an empty-shape entry so the wire-format question count matches args.questions —
      // the backend's DetermineAnswerSentinel treats all-empty answers as "User skipped"
      // (mixed cases serialise as "User answered" with empty entries for the skipped ones).
      const answers: AskAnswer[] = [];
      for (const q of args.questions) {
        if (skipped[q.id]) {
          answers.push({ questionId: q.id, selectedOptionIds: [], freeText: null, files: [] });
          continue;
        }
        const s = states[q.id] ?? emptyState();
        const rawIds = Array.from(s.selectedIds);
        const openSelected = rawIds.includes(OPEN_OPTION_ID);
        // Strip the UI-only sentinel — never sent to the backend.
        const selectedOptionIds = rawIds.filter(id => id !== OPEN_OPTION_ID);
        const files: AnswerFile[] = [];
        for (const optId of selectedOptionIds) {
          const file = s.filesByOption[optId] ?? null;
          if (!file) continue;
          const fd = new FormData();
          fd.append("file", file);
          const uploadRes = await apiAuthFetch("api/files/upload", { method: "POST", body: fd });
          if (!uploadRes.ok) throw new Error(t("ask.uploadFailed", { status: uploadRes.status }));
          const json = await uploadRes.json();
          files.push({
            optionId: optId,
            fileId: json.fileId,
            fileName: json.fileName ?? file.name,
            note: (s.notesByOption[optId] ?? "").trim() || null,
          });
        }
        const trimmedFree = s.freeText.trim();
        answers.push({
          questionId: q.id,
          selectedOptionIds,
          freeText: openSelected && trimmedFree ? trimmedFree : null,
          files,
        });
      }

      const submitRes = await submitToolCallResult(toolCallId, { answers });
      if (submitRes.kind === "stale") {
        // 410 from the result endpoint: tool call no longer known to the server (TTL
        // expired or duplicate submit). Surface a clear message — the LLM is no longer
        // waiting on this card.
        ClientLogger.warn("Ask submit returned stale (410)", {
          component: "AskCard",
          action: "submitToolCallResult",
          metadata: { toolCallId, questionCount: args.questions.length },
        });
        throw new Error(t("ask.error409"));
      }
      if (submitRes.kind === "error") {
        // Log with the actual HTTP status so console debugging works — pre-Phase-1.5
        // we passed res.status through to the toast and there was no log. Now we have
        // both: a structured ClientLogger entry AND the i18n status placeholder.
        ClientLogger.error("Ask submit POST failed", {
          component: "AskCard",
          action: "submitToolCallResult",
          metadata: {
            toolCallId,
            status: submitRes.status,
            questionCount: args.questions.length,
          },
        });
        if (submitRes.status === 409) throw new Error(t("ask.error409"));
        throw new Error(t("ask.submitFailed", { status: submitRes.status }));
      }

      // Unmount the card immediately — before the next-turn append kicks off. The new
      // turn's StartEvent races the user seeing the card still up; flipping the submitted
      // latch keeps it clean.
      onSubmitted();

      // Hybrid in-loop wait: if the server signalled continuation, the agent loop keeps
      // running in the same turn — the SSE stream is still open, text-delta events will
      // continue. Do NOT append a user message or start a new /chat turn.
      if (submitRes.continuation?.continuation) {
        ClientLogger.info("Tool-call result accepted (continuation)", {
          component: "AskCard",
          action: "submitResult",
          metadata: { toolCallId, continuation: true },
        });
        return;
      }

      ClientLogger.info("Tool-call result accepted (new turn)", {
        component: "AskCard",
        action: "submitResult",
        metadata: { toolCallId, continuation: false },
      });

      // Append the Q:/A: prose as a user message, which triggers the next /chat turn.
      const prose = buildProse(states, skipped);
      aui.thread().append({
        role: "user",
        content: [{ type: "text", text: prose }],
      });
    } catch (err) {
      // Telemetry: log the thrown error so console debugging works for the "Odeslání
      // selhalo:" UI message — pre-fix the catch swallowed everything and only the i18n
      // string surfaced.
      ClientLogger.error("Ask submit threw", {
        error: err instanceof Error ? err : undefined,
        component: "AskCard",
        action: "onSubmit",
        metadata: { toolCallId, questionCount: args.questions.length },
      });
      setErrorMessage((err as Error).message);
      setSubmitting(false);
    }
  }, [args.questions, aui, buildProse, canSubmit, onSubmitted, skipped, states, submitting, t, toolCallId]);

  return (
    <div
      className="aui-ask-card flex w-full flex-col gap-4 rounded-lg bg-background p-4 text-sm"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key !== "Enter" || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        // Critical: ask-card has nested <textarea> for file notes and free-text answers.
        // Enter inside those (and inside any <input>) must keep its native behaviour
        // (newline / form-control activation), not advance/submit the card.
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === "TEXTAREA" || tag === "INPUT") return;
        e.preventDefault();
        if (isLast && canSubmit) void onSubmit();
        else if (canAdvance) goNext();
      }}
    >
      <StepperHeader
        show={total > 1}
        counterLabel={t("ask.stepIndicator", { current: stepIndex + 1, total })}
        disabled={submitting}
        dots={args.questions.map((q, i) => ({
          key: q.id,
          ariaLabel: `${q.header} (${i + 1}/${total})`,
          state: i === stepIndex
            ? "active"
            : isDecided(q)
              ? "done"
              : "pending",
          onClick: () => setStepIndex(i),
        }))}
      />

      <section key={current.id} className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
            {current.header}
          </span>
          <p className="font-medium text-foreground">{current.question}</p>
        </div>

        <ul className="flex flex-col gap-2">
          {current.options.map(opt => {
            const selected = curState.selectedIds.has(opt.id);
            return (
              <li
                key={opt.id}
                className={cn(
                  "rounded-md px-3 py-2 transition-colors cursor-pointer",
                  selected ? "bg-blue-50/40" : "hover:bg-muted/50",
                )}
                onClick={() => { if (!submitting && !curSkipped) toggle(opt.id); }}
              >
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type={multi ? "checkbox" : "radio"}
                    name={`ask-${toolCallId}-${current.id}`}
                    checked={selected}
                    onChange={() => toggle(opt.id)}
                    disabled={submitting || curSkipped}
                    className="mt-1"
                  />
                  <span className="flex-1">{opt.label}</span>
                </label>

                {selected && opt.allowFile && (
                  <div className="mt-2 flex flex-col gap-2 pl-6">
                    <label className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                      <PaperclipIcon className="size-3" />
                      <span>
                        {curState.filesByOption[opt.id]?.name ??
                          (opt.fileRequired
                            ? t("ask.attachRequired")
                            : t("ask.attachOptional"))}
                      </span>
                      <input
                        type="file"
                        accept={ACCEPTED_EXTENSIONS}
                        className="hidden"
                        disabled={submitting || curSkipped}
                        onChange={e => setFile(opt.id, e.target.files?.[0] ?? null)}
                      />
                    </label>
                    <textarea
                      placeholder={t("ask.notePlaceholder")}
                      rows={2}
                      value={curState.notesByOption[opt.id] ?? ""}
                      onChange={e => setNote(opt.id, e.target.value)}
                      disabled={submitting || curSkipped}
                      className="w-full resize-none rounded border bg-background px-2 py-1 text-xs outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
                    />
                  </div>
                )}
              </li>
            );
          })}

          {/* Synthetic "type your own answer" row — same visual style as real options.
              Sentinel id OPEN_OPTION_ID is stripped from the payload on submit. */}
          <li
            className={cn(
              "rounded-md px-3 py-2 transition-colors cursor-pointer",
              openSelected ? "bg-blue-50/40" : "hover:bg-muted/50",
            )}
            onClick={() => { if (!submitting && !curSkipped) toggle(OPEN_OPTION_ID); }}
          >
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type={multi ? "checkbox" : "radio"}
                name={`ask-${toolCallId}-${current.id}`}
                checked={openSelected}
                onChange={() => toggle(OPEN_OPTION_ID)}
                disabled={submitting || curSkipped}
                className="mt-1"
              />
              <span className="flex-1 text-muted-foreground">{t("ask.openOptionLabel")}</span>
            </label>

            {openSelected && (
              <div className="mt-2 pl-6">
                <textarea
                  rows={3}
                  autoFocus
                  value={curState.freeText}
                  onChange={e => setFreeText(e.target.value)}
                  disabled={submitting || curSkipped}
                  placeholder={t("ask.openPlaceholder")}
                  className="w-full resize-none rounded border bg-background px-2 py-1 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
                />
              </div>
            )}
          </li>
        </ul>

      </section>

      {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}

      <StepperFooter
        back={{
          label: t("ask.back"),
          onClick: goBack,
          disabled: stepIndex === 0 || submitting,
        }}
        middle={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={skipCurrent}
            disabled={submitting || curSkipped}
          >
            {t("ask.skip")}
          </Button>
        }
        primary={isLast
          ? {
              label: submitting ? t("ask.submitting") : t("ask.submit"),
              onClick: onSubmit,
              disabled: !canSubmit || submitting,
            }
          : {
              label: t("ask.next"),
              onClick: goNext,
              disabled: !canAdvance || submitting,
            }}
      />
    </div>
  );
};
